import { Drawer, DrawerContent, DrawerOverlay, Flex } from "@chakra-ui/core"
import axios from "axios"
import { navigate } from "gatsby"
import isNil from "lodash/isNil"
import omit from "lodash/omit"
import React, { useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import DropDownSelector from "../../components/DropDownSelector"
import EditAnswers from "../../components/Quiz/EditAnswers"
import QuizResults from "../../components/Quiz/QuizResults"
import { BaseDispatch, BaseRootState } from "../../redux/store"
import InitialQuizScreen from "./InitialQuizScreen"
import PreviousResults from "./PreviousResults"
import QuizQuestions from "./QuizQuestions"
import SidebarContainer from "./SidebarContainer"
// Styles
import { subscribeNewsletter } from "../../api/subscribeNewsletter"
import config from "../../config"
import useLoginDrawer from "../../context/loginDrawer"
import { isValidUserLoggedIn } from "../../utils/auth"
import { prepareKlaviyoResults } from "../../utils/bodyFit"
import { bp } from "../../utils/MediaQueries"
import "../SideBar/styles/css/BodyFitSidebarDesktop.css"
import * as utils from "./utils"

interface MobileQuizDrawerProps {
  isMobileQuizDrawerOpen: boolean
  currentQuestionNumber: number
  totalQuestionNumber: number
  canGoToNextQuestion: boolean
  onOpen: VoidFunction
  onClose: VoidFunction
  onNextQuestion: VoidFunction
  onPrevQuestion: VoidFunction
  locationState?: {
    fromQuizResults: {}
  }
  quizIntroText?: string
}

const BodyFitQuizBox: React.FC<
  {
    isSidebarOpen: boolean
    toggleSidebar: VoidFunction
    currentPagePath: string
    boxQuizImage?: any
  } & MobileQuizDrawerProps
> = ({ currentPagePath, boxQuizImage, onClose, quizIntroText }) => {
  const {
    quiz: {
      quizProgressState,
      previousQuizAnswers,
      answers,
      questions,
      discount,
      currentPreviousAnswersDate,
    },
    bikes: { selectedBike, bikeResults },
    sidebar: { sideBarStep },
    user: { email, accessToken, tokenExpiration, ...user },
  } = useSelector((state: BaseRootState) => state)
  const dispatch = useDispatch<BaseDispatch>()
  const [quizDropDownOptions, setQuizDropDownOptions] = React.useState<
    Array<{ title: string; name: string; selected: boolean }>
  >([])
  const [isSubmittingResults, setSubmittingResults] = useState(false)
  const { openLoginDrawer } = useLoginDrawer()
  const isBikeSelectPage = currentPagePath === "/quiz-results"
  const isCustomizerPage = currentPagePath?.startsWith("/customizer/")

  const isLoggedIn = isValidUserLoggedIn({
    accessToken,
    tokenExpiration,
  })

  const [editingQuestion, setEditingQuestions] = React.useState<boolean>(false)
  const [editedQuestions, setEditedQuestions] = React.useState({})
  const initializedPrevQuestions = React.useRef(false)

  // Sync quiz results dropdown
  React.useEffect(() => {
    setQuizDropDownOptions(
      previousQuizAnswers.map((a) => {
        const d = new Date(parseInt(a.date, 10))
        const formatedDate = utils.dateToPretty(d)
        return {
          title: formatedDate,
          name: a.date,
          selected: a.date === currentPreviousAnswersDate,
        }
      })
    )
  }, [previousQuizAnswers, currentPreviousAnswersDate])

  const [tags, setTags] = React.useState<Array<string>>(new Array(3))
  const getAnswers = (date) => {
    return (
      previousQuizAnswers.find((a) => a.date === date) ?? {
        date: "",
        riderType: "",
      }
    )
  }

  React.useEffect(() => {
    if (currentPreviousAnswersDate !== null) {
      const currentAnswers = getAnswers(currentPreviousAnswersDate)
      if (currentAnswers.date !== "") {
        const weightValue = currentAnswers?.["weight"]?.[0]
        const heightFtValue = currentAnswers?.["height"]?.[0]
        const heightInValue = currentAnswers?.["height"]?.[1]
        const weight = weightValue ? `${weightValue}lbs` : ""
        const height =
          !isNil(heightFtValue) && !isNil(heightInValue)
            ? `${heightFtValue}ft ${heightInValue}in`
            : ""
        let bikeType = ""
        switch (currentAnswers["standard-or-electric"]) {
          case "standard-or-electric-standard-bicycle":
            bikeType = "Standard"
            break
          case "standard-or-electric-electric-bicycle":
            bikeType = "Electric"
            break
          case "standard-or-electric-both":
            bikeType = "Stand/Elec"
            break
        }
        setTags([height, weight, bikeType])
      }
    }
  }, [currentPreviousAnswersDate, previousQuizAnswers])

  React.useEffect(() => {
    if (!initializedPrevQuestions.current) {
      const loadDefaultPreviousQuestions = async () => {
        initializedPrevQuestions.current = true
        if (previousQuizAnswers.length > 0) {
          if (answers.date === "") {
            const quizAnswers = currentPreviousAnswersDate
              ? previousQuizAnswers.find(
                  (answers) => answers.date === currentPreviousAnswersDate
                ) || previousQuizAnswers[0]
              : previousQuizAnswers[0]
            await dispatch.quiz.setAnswers(quizAnswers)
            dispatch.bikes.calculateResults()
          } else if (answers.riderType === "") {
            dispatch.bikes.calculateResults()
          }
        }
      }
      loadDefaultPreviousQuestions()
    }
  }, [answers, previousQuizAnswers])

  React.useEffect(() => {
    if (isBikeSelectPage) {
      dispatch.sidebar.setSideBarStep("select-bike")
    } else if (isCustomizerPage || selectedBike) {
      dispatch.sidebar.setSideBarStep("customize")
    } else {
      dispatch.sidebar.setSideBarStep("fit")
    }
  }, [isBikeSelectPage, isCustomizerPage, selectedBike])

  const startNewQuiz = () => {
    dispatch.quiz.resetQuiz()
    dispatch.quiz.setProgressState("initial")
    dispatch.quiz.setAnswers({ date: Date.now().toString(), riderType: "" })
  }
  const onExitQuiz = () => {
    initializedPrevQuestions.current = false
    dispatch.quiz.resetQuiz()
    dispatch.quiz.setProgressState("initial")
  }

  const onEditAnswers = () => {
    setEditingQuestions(true)
  }
  const onExitEditingAnswers = () => {
    setEditingQuestions(false)
  }
  const onSaveEditedAnswers = async () => {
    const newPreviousAnswers = previousQuizAnswers.map((answer) =>
      answer.date === currentPreviousAnswersDate ? answers : answer
    )
    await dispatch.quiz.setPreviousAnswers(newPreviousAnswers)
    await dispatch.quiz.setAnswers(
      newPreviousAnswers.find(
        ({ date }) => currentPreviousAnswersDate === date
      ) ?? { date: "", riderType: "" }
    )
    const updatedBikeResults: any = await dispatch.bikes.calculateResults()

    const isPreviousQuestionsLoaded =
      (currentPreviousAnswersDate && currentPreviousAnswersDate !== "") ||
      answers.date !== ""
    if (isLoggedIn && isPreviousQuestionsLoaded) {
      axios.put(`${config.FIREBASE_FUNCTIONS_URL}/quizResults`, {
        timestamp: new Date(
          currentPreviousAnswersDate && currentPreviousAnswersDate !== ""
            ? parseInt(currentPreviousAnswersDate, 10)
            : answers.date
        ).toISOString(),
        questionAnswerPairs: editedQuestions,
        shopifyToken: accessToken,
        bikeResults: updatedBikeResults && updatedBikeResults.length ? updatedBikeResults.slice(0, 15).map((bike) => bike.title).join('||') : '',
      })
    }
  }

  const onAnsweredEdited = (
    questionId: string,
    answerId: string | Array<string>
  ) => {
    dispatch.quiz.answerQuestion({ questionId, answerId })
    setEditedQuestions({ ...editedQuestions, ...{ [questionId]: answerId } })
  }

  const onMyMatches = async () => {
    dispatch.sidebar.setSideBarStep("select-bike")
    await dispatch.quiz.setAnswers(getAnswers(currentPreviousAnswersDate))
    dispatch.bikes.calculateResults()
    dispatch.bikes.setProgressState("editing")
    onClose()
    navigate("/quiz-results")
  }

  const submitQuizResults = async () => {
    setSubmittingResults(true)
    const timestamp = new Date(
      answers.date !== "" ? parseInt(answers.date, 10) : Date.now()
    ).toISOString()
    
    try {
      const newAnswers = {
        ...omit(answers, ["date", "sms-consent"]),
        sentToDB: "true",
      }
      const updatedBikeResults: any = await dispatch.bikes.calculateResults()
      
      await axios.post(`${config.FIREBASE_FUNCTIONS_URL}/quizResults`, {
        questionAnswerPairs: newAnswers,
        shopifyToken: accessToken,
        timestamp,
        bikeResults: updatedBikeResults && updatedBikeResults.length ? updatedBikeResults.slice(0, 15).map((bike) => bike.title).join('||') : '',
      })
      await dispatch.quiz.setAnswers({ ...answers, sentToDB: "true" })
    } catch (error) {
      await dispatch.quiz.setAnswers({ ...answers, sentToDB: "false" })
    } finally {
      await dispatch.quiz.setCurrentPreviousAnswersDate(answers.date)
      await dispatch.quiz.setPreviousAnswers([
        ...(previousQuizAnswers || []),
        answers,
      ])
      onClose()

      // Klaviyo Quiz submit
      const bikeResults = await dispatch.bikes.calculateResults()
      try {
        const email = answers["email"] as string
        const phoneOptIn = answers["sms-consent"] === "yes"
        const phone = answers["phone"] as string
        if (email) {
          const data = {
            listId: config.GATSBY_KLAVIYO_QUIZ_LIST_ID,
            email: email,
            ...(await prepareKlaviyoResults({
              bikes: bikeResults as any,
              name: user.name || undefined,
              phone: phone || undefined,
            })),
            sms_consent: phoneOptIn,
          }
          await subscribeNewsletter(data)
        }
      } catch (e) {
        console.error(e.message)
      }
    }
    try {
      const unixTimestamp = new Date(timestamp).getTime()

      const result = await axios.post(`${config.FIREBASE_FUNCTIONS_URL}/createDiscount`, {
        code: "QUIZ" + unixTimestamp,
        discount: discount,
        startDate: timestamp,
        endDate: new Date(unixTimestamp + 24 * 60 * 60 * 1000)
      })
    } catch (e) {
      console.log(e)
    }
    setSubmittingResults(false)
  }

  const onPreviousQuizOptionSelected = async (date: string) => {
    await dispatch.quiz.setCurrentPreviousAnswersDate(date)
    await dispatch.quiz.setAnswers(getAnswers(date))
    dispatch.bikes.calculateResults()
  }

  const onSeeResults = () =>
    submitQuizResults().then(() => {
      dispatch.sidebar.setSideBarStep("select-bike")
      navigate("/quiz-results", {
        state: { previousPath: currentPagePath },
      })
    })

  const showPreviousResults =
    quizProgressState === "previous" && previousQuizAnswers.length > 0
  const showQuizQuestions =
    quizProgressState !== "initial" &&
    quizProgressState !== "previous" &&
    quizProgressState !== "finished"
  const showResults = quizProgressState === "finished"
  const showInitialQuiz =
    quizProgressState === "initial" ||
    (!showPreviousResults && !showQuizQuestions && !showResults) // show something if nothing is available

  // Mens Bicycle BG
  let bgImage = `/images/Mens-BodyFit-BG.png`
  // Womens Bicycle BG
  if (!boxQuizImage) {
    bgImage = `/images/Womens-BodyFit-BG.png`
  }

  let bgImageStyle = {}

  if (quizProgressState === "initial") {
    bgImageStyle = {
      backgroundColor: "transparent !important",
      backgroundImage: `url(${bgImage})`,
      backgroundSize: "contain",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "left bottom",
    }
  }

  return (
    <Flex
      background={"#005f78"}
      width="100%"
      minH={"330px"}
      height={bp("auto", "88%")}
      borderRadius="20px"
      className="BoxBodyFitQuiz"
      style={bgImageStyle}
    >
      <SidebarContainer
        position={1}
        title=""
        active={sideBarStep === "fit"}
        onHeaderClick={() => {
          dispatch.sidebar.setSideBarStep("fit")
          if (quizProgressState === "finished") {
            dispatch.quiz.setProgressState("previous")
          }
        }}
        rightComponent={
          sideBarStep === "fit" && showPreviousResults ? (
            <DropDownSelector
              options={quizDropDownOptions}
              dropDownType="single_top"
              color="#fff"
              textTransform="uppercase"
              itemProps={{
                textTransform: "uppercase",
              }}
              showCheckMarks={false}
              onOptionSelected={onPreviousQuizOptionSelected}
            />
          ) : undefined
        }
      >
        <Drawer
          isOpen={editingQuestion}
          placement="right"
          size="md"
          onClose={onExitEditingAnswers}
        >
          <DrawerOverlay />
          <DrawerContent bg="none" boxShadow="none" zIndex={1000001}>
            <Flex className="QuizEditAnswerSection" h="100%">
              <EditAnswers
                onExit={onExitEditingAnswers}
                onSave={onSaveEditedAnswers}
                onAnswerEdit={onAnsweredEdited}
                formatedDate={
                  currentPreviousAnswersDate
                    ? utils.dateToPretty(
                        new Date(
                          parseInt(currentPreviousAnswersDate ?? "", 10)
                        ),
                        false,
                        true
                      )
                    : ""
                }
                questions={questions.map((q) => {
                  let answer = answers[q.questionId]
                  return {
                    ...q,
                    answer,
                  }
                })}
                answers={answers}
              />
            </Flex>
          </DrawerContent>
        </Drawer>
        {showPreviousResults && (
          <PreviousResults
            className="BodyFitLayoutPreviousResultsContainer"
            riderStyle={answers.riderType !== "" ? answers.riderType : null}
            tags={tags}
            nextText="My Matches"
            onNext={onMyMatches}
            onNewQuiz={startNewQuiz}
            onEditAnswers={onEditAnswers}
            style={{ padding: "1.3rem 2rem" }}
          />
        )}
        {showInitialQuiz && <InitialQuizScreen quizIntroText={quizIntroText} />}
        {showQuizQuestions && (
          <QuizQuestions
            className="BodyFitLayoutQuestionContainer"
            defaultEmail={email || undefined}
            onExit={onExitQuiz}
            style={{ padding: "1.7rem" }}
          />
        )}
        {showResults && (
          <QuizResults
            className="BodyFitLayoutResultContainer"
            isLoggedIn={isLoggedIn}
            isSubmittingResults={isSubmittingResults}
            onLogin={openLoginDrawer}
            onSeeResults={onSeeResults}
            style={{ padding: "2rem" }}
          />
        )}
      </SidebarContainer>
    </Flex>
  )
}

export default BodyFitQuizBox
